import React, { useMemo, useRef, useState } from 'react';
import { Button, Pagination } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SettingsIcon from '@mui/icons-material/Settings';
import { AppResultInfo, AppSkinProps } from '../../../../types/decisions';
import BasicTabs from '../../../tabs/tabs';
import Modal from '../../../modal/Modal';
import CriteriaSetting from '../../app-components/CriteriaSetting';
import { TriangleSlider } from '../../../slider';
import Loading from '../../../loading/Loading';
import { getCriteriaFieldsToDisplayInResults, getFieldName } from '../../../../utils';
import { default as CriteriaSettingsV2 } from '../../app-components/CriteriaSettings';
import { useAppConfig } from '../../../../context/app-configs-context';
import { ResultProductCardSpecs } from '../../app-components/AppResults/AppResults';
import './MobileSkin1.scss';
import AppInfo from '../../app-components/AppInfo';
import { useSearchParams } from 'react-router-dom';

const MobileSkin1 = ({
    results,
    onRerank,
    criteriaLoading,
    criteriaData,
    onSliderChange,
    onCriteriaApply,
    resultLoading,
    onPageChange,
    pageInfo,
    onTradeoffValueChange,
    fieldInitialValues,
}: AppSkinProps) => {
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const { appConfigs: { criteriaSettings: { popup } } } = useAppConfig();
    const [ open, setOpen ] = useState(false);
    const [ modalContent, setModalContent ] = useState<React.ReactElement>(<></>);
    const [ activeTab, setActiveTab ] = useState(0);
    const criteriaChangedOnceRef = useRef<{
        [key: string]: boolean
    }>({});
    const displayFieldsIds: string[] = useMemo(() => {
        return getCriteriaFieldsToDisplayInResults(criteriaData.fields)
        .map(item => item.id);
    }, [ criteriaData.fields ]);


    const onAutoPopupCheck = (newImportance: number, id: string) => {
        if (!popup.show || newImportance < popup.threshold) {
            return;
        }

        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            openCriteriaSettings(id);
        }
    };

    const handleClose = () => {
        setModalContent(<></>);
        setOpen(false);
    };

    const openCriteriaSettings = (id: string) => {
        const field = criteriaData.fields.find(f => f.id === id);
        if (!field || field.hide_field) {
            return;
        }
        setOpen(true);
        setModalContent(
            <CriteriaSetting
                field={field}
                onSliderChange={onSliderChange}
                onTradeoffValueChange={onTradeoffValueChange}
                fieldInitialValues={fieldInitialValues}
                handleModalClose={handleClose}
                areCTAsDisplayed
            />,
        );
    };

    const onMobileRerank = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, resultId: string) => {
        e.preventDefault();
        e.stopPropagation();
        onRerank(resultId, dataset, true);
    };

    const openProductDetailsSettings = (product: AppResultInfo) => {
        const name = getFieldName(product.data.name);
        const productLink = product.data.productlink?.value;
        const actionLink = product.data.actionlink?.value;
        const actionCTAName = product.data.actionbuttontext?.value;
        setOpen(true);
        setModalContent(
            <div className="new-app-results-container">
                <div className="new-app-results">
                    <div className="threeColumns">
                        <div className="new-results-item">
                            <div className="image-container" style={{ backgroundImage: `url('${product.icon}')` }}/>
                            <div className="new-results-item-details">
                                <div className="new-results-item-details-rank">
                                    <div className="new-results-item-details-rank-bar"
                                         style={{ width: product.rank + '%' }}/>
                                    <div className="new-results-item-details-rank-rate">
                                        {product.rank === '-' ? <>&nbsp;</> : product.rank + '%'}
                                    </div>
                                </div>
                                <div>
                                    <h3
                                        className={`truncate ${productLink ? 'product-link' : ''}`}
                                        title={productLink || name}
                                        onClick={() => productLink && window.open(productLink, '_blank')}
                                    >
                                        {name}
                                    </h3>
                                    <ResultProductCardSpecs
                                        id={product.id}
                                        nonTechnicalFields={product.nonTechnicalFields}
                                        allowedFieldsIds={displayFieldsIds}
                                    />
                                </div>
                                <div className="new-results-item-details-ctas">
                                    {actionLink && actionCTAName && (
                                        <Button
                                            className="new-results-item-details-ctas-cta action-link"
                                            variant="outlined"
                                            onClick={() => window.open(actionLink, '_blank')}
                                        >
                                            {actionCTAName}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>,
        );
    };

    return (
        <div className="mobile-application application">
            {criteriaLoading ? (
                <Loading/>
            ) : (
                <>
                    <AppInfo
                        icon={criteriaData.icon}
                        name={criteriaData.name}
                        description={criteriaData.description}
                    />
                    <div className="application-details">
                        <div className="app-criteria" id="app-criteria">
                            {criteriaLoading ? (
                                <Loading/>
                            ) : (
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setActiveTab(0);
                                    onCriteriaApply(dataset);
                                }}>
                                    <BasicTabs
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                        tabNames={[ 'Results', 'Criteria', <SettingsIcon/> ]}
                                        tabs={[
                                            <div className="app-results">
                                                {resultLoading ? (
                                                    <Loading/>
                                                ) : (
                                                    <>
                                                        {/*<h3>Results ({pageInfo.total})</h3>*/}
                                                        <div>
                                                            {results.length === 0 ? (
                                                                <div style={{ color: '#676767' }}>
                                                                    No products found.
                                                                </div>
                                                            ) : results.map((result, index) => {
                                                                const actionLink = result.data.actionlink?.value;
                                                                const actionCTAName = result.data.actionbuttontext?.value;
                                                                let shouldDisplayInferButton = true;
                                                                if (index === 0 && pageInfo.current_page === 1) {
                                                                    shouldDisplayInferButton = false;
                                                                }

                                                                return (
                                                                    <div
                                                                        className="app-results-item"
                                                                        key={result.id}
                                                                        onClick={() => openProductDetailsSettings(result)}
                                                                    >
                                                                        <div className="image-container"
                                                                             style={{ backgroundImage: `url('${result.icon}')` }}/>
                                                                        <div className="app-criteria-item-details">
                                                                            <div
                                                                                className="app-criteria-item-details-rank">
                                                                                <div
                                                                                    className="app-criteria-item-details-rank-bar"
                                                                                    style={{ width: result.rank + '%' }}/>
                                                                                <div
                                                                                    className="app-criteria-item-details-rank-rate">
                                                                                <span className="truncate">
                                                                                    {getFieldName(result.data.name)}
                                                                                </span>
                                                                                    <span>
                                                                                    {result.rank === '-' ? '' : result.rank + '%'}
                                                                                </span>
                                                                                </div>
                                                                            </div>
                                                                            <div>
                                                                                {/*<h3*/}
                                                                                {/*        className='truncate'*/}
                                                                                {/*        title={typeof result.data.name !== 'object' ? result.data.name : result.data.name.value}*/}
                                                                                {/*>*/}
                                                                                {/*    {typeof result.data.name !== 'object' ? result.data.name : result.data.name.value}*/}
                                                                                {/*</h3>*/}
                                                                                {/*<div className='app-criteria-item-details-specs'></div>*/}
                                                                            </div>
                                                                            <div
                                                                                className="app-criteria-item-details-ctas">
                                                                                {actionLink && actionCTAName && (
                                                                                    <Button
                                                                                        className="new-results-item-details-ctas-cta action-link"
                                                                                        variant="outlined"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            window.open(actionLink, '_blank');
                                                                                        }}
                                                                                    >
                                                                                        {actionCTAName}
                                                                                    </Button>
                                                                                )}
                                                                                {shouldDisplayInferButton && (
                                                                                    <Button
                                                                                        className="app-criteria-item-details-ctas-cta"
                                                                                        variant="outlined"
                                                                                        style={{ marginLeft: 8 }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            onMobileRerank(e, result.id);
                                                                                        }}
                                                                                        startIcon={<ArrowUpwardIcon/>}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <div className="app-results-page-info">
                                                            {/*Displayed products: <b>{pageInfo.start}</b> to <b>{pageInfo.end}</b> of <b>{pageInfo.total}</b>.*/}
                                                            {results.length > 0 ? (
                                                                <Pagination
                                                                    page={pageInfo.current_page}
                                                                    count={pageInfo.total_pages}
                                                                    shape="rounded"
                                                                    onChange={(_, page) => {
                                                                        onPageChange(dataset, page);
                                                                        window.scrollTo({
                                                                            top: 0,
                                                                            behavior: 'smooth',
                                                                        });
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </>
                                                )}
                                            </div>,
                                            <div>
                                                {criteriaData.fields.map(({
                                                    id,
                                                    name,
                                                    hide_importance,
                                                    hide_field,
                                                }, idx) => (
                                                    <div key={id}
                                                         className={`app-criteria-field ${hide_importance ? 'hidden' : ''}`}>
                                                        <label>{getFieldName(name)}
                                                            <TriangleSlider
                                                                name={id}
                                                                min={0}
                                                                max={100}
                                                                step={1}
                                                                value={+criteriaData.fields[idx].importance}
                                                                onChange={(_, value) => onSliderChange(value as number, id)}
                                                                onChangeCommitted={(_, value) => onAutoPopupCheck(value as number, id)}
                                                                isEditable
                                                                isEditingDisabled={hide_field}
                                                                onEditIconClick={() => openCriteriaSettings(id)}
                                                            />
                                                        </label>
                                                    </div>
                                                ))}
                                                <div className="app-criteria-actions" style={{ display: 'block' }}>
                                                    <div>
                                                        <Button
                                                            className="app-criteria-actions-action"
                                                            type="submit"
                                                            variant="contained"
                                                            style={{ width: '100%' }}
                                                        >
                                                            Decide
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>,
                                            <div>
                                                <CriteriaSettingsV2
                                                    criteriaFields={criteriaData.fields}
                                                    onSliderChange={onSliderChange}
                                                    onTradeoffValueChange={onTradeoffValueChange}
                                                    fieldInitialValues={fieldInitialValues}
                                                    handleClose={handleClose}
                                                    openCriteriaSettings={openCriteriaSettings}
                                                />
                                                <div className="app-criteria-actions" style={{ display: 'block' }}>
                                                    <div>
                                                        <Button
                                                            className="app-criteria-actions-action"
                                                            type="submit"
                                                            variant="contained"
                                                            style={{ width: '100%' }}
                                                        >
                                                            Decide
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>,
                                        ]}
                                    />
                                </form>
                            )}
                        </div>
                    </div>
                </>
            )}
            <Modal isOpen={open} handleClose={handleClose}>
                {modalContent}
            </Modal>
        </div>
    );
};

export default MobileSkin1;